import _ from 'lodash';
import {
  CALCULATION_CONTENT_CONTAINER_ID,
  RANGE_END_ID,
  RANGE_START_ID,
} from '../../../shared/constants';
import convertFromRange from './convertFromRange';
import preprocessRange from './preprocessRange';
import getValidatedContentArea from '../../marks/functions';
import { ContentArea, ContentAreaTagName } from '../types';

export interface ITagFreeContentProps {
  content: string;
  indexes: number[];
}

interface IPositionProps {
  start: number;
  end: number;
}

interface IMatchProps {
  word: string;
  position: IPositionProps;
}

interface ISuggestionProps {
  position: IPositionProps;
  lengthDelta: number;
  missingWordsCount: number;
  content?: string;
}

interface ISuggestionCombinationProps {
  position: IPositionProps;
  content: string;
}

type SuggestionSortKeys = keyof ISuggestionProps;

const missingWordsCountPropName: SuggestionSortKeys = 'missingWordsCount';
const lengthDeltaPropName: SuggestionSortKeys = 'lengthDelta';

/**
 * Checks if the suggested substring already contains a `ContentArea` element (or parts of it).
 *
 * @param {number} suggestionStart - The start of the pattern within the HTML content string.
 * @param {number} suggestionEnd - The end of the pattern within the HTML content string.
 * @param {string} content - The HTML content string.
 * @param {'mark' | 'a'} tagName - The tag name of the element that is not allowed to overlap with the suggestion.
 * @returns {boolean} A boolean indicating whether the pattern overlaps with an existing `ContentArea` element.
 */
const getIntersectsExistingArea = (
  suggestionStart: number,
  suggestionEnd: number,
  content: string,
  tagName: 'mark' | 'a',
) => {
  const startTagRegex = new RegExp(`<${tagName}(.*?)>`, 'g');
  const endTagRegex = new RegExp(`</${tagName}>`, 'g');
  const pattern = content.substring(suggestionStart, suggestionEnd);
  const contentBeforePattern = content.substring(0, suggestionStart);
  const contentAfterPattern = content.substring(
    suggestionEnd,
    content.length - 1,
  );

  const patternHasExistingArea =
    startTagRegex.test(pattern) || endTagRegex.test(pattern);
  const patternStartsInsideExistingArea =
    (Array.from(contentBeforePattern.matchAll(startTagRegex)).length ?? 0) !==
    (Array.from(contentBeforePattern.matchAll(endTagRegex)).length ?? 0);
  const patternEndsInsideExistingArea =
    (Array.from(contentAfterPattern.matchAll(startTagRegex)).length ?? 0) !==
    (Array.from(contentAfterPattern.matchAll(endTagRegex)).length ?? 0);

  return (
    patternHasExistingArea ||
    patternStartsInsideExistingArea ||
    patternEndsInsideExistingArea
  );
};

/**
 * Creates an object that contains: 1. the tag-free content, and 2. an array with the indexes of the characters in the tag-free content.
 * All tags in the content are matched using regex and then removed from the content string as well as the corresponding positions in the index array.
 * This allows pattern search operations to be performed on the string while the position in the original content string can still be recovered using `indexes[charposition]`.
 *
 *  ![Example](../../../../docs/images/tagFreeContent.png)
 *
 * @param {string} content - The HTML content string.
 * @returns {ITagFreeContentProps} {@link ITagFreeContentProps} Object with 1. the tag-free content, and 2. an array with the original character positions.
 */
export const getTagfreeContent = (content: string): ITagFreeContentProps => {
  const htmlTagRegex = /<("[^"]*"|'[^']*'|[^'">])*>/g;
  let c = content;
  let i = Array.from({ length: content.length + 1 }, (_value, index) => index);
  // reverse matches and start with the removing at the end of the string to keep the indexes of the matches intact
  const allMatches = [...c.matchAll(htmlTagRegex)].reverse();

  allMatches.forEach((match) => {
    if (match.index || match.index === 0) {
      c =
        c.substring(0, match.index) +
        c.substring(match.index + match[0].length, c.length);
      i = i
        .slice(0, match.index)
        .concat(i.slice(match.index + match[0].length));
    }
  });

  return { content: c, indexes: i };
};

/**
 * Since the pattern might have lost words or their order might have changed, the pattern is split so we can
 * search for the individual words.
 *
 * @param {string} pattern - The text that was marked or the link was set on.
 * @returns {string[]} The pattern split into individual words.
 */
const getPatternWordList = (pattern: string): string[] => {
  if (!pattern.length) {
    return [];
  }

  const regex = /(?:\p{L}|['\-_[\]()§]|\d)+/gu;
  return pattern.match(regex) || [];
};

const getDistinctPatternListIgnoreCase = (patternList: string[]): string[] =>
  _.uniq(patternList.map((p) => p.toLowerCase()));

/**
 * Finds the matches that are used to generate a suggestion.
 *
 * @param {ITagFreeContentProps} tagFreeContent - The content without tags and its index list containing the index where each character will be positioned when there are tags in between.
 * @param {string} pattern - The full pattern of the content area.
 * @param {string[]} patternList - The pattern split into individual words.
 * @returns {Array} A list containing all matches for the full pattern or all individual words.
 */
const getMatchList = (
  tagFreeContent: string,
  pattern?: string,
  patternList?: string[],
): IMatchProps[] => {
  const matchList: IMatchProps[] = [];
  const patternWordList = patternList?.length
    ? getDistinctPatternListIgnoreCase(patternList)
    : [...new Set(getPatternWordList(pattern || ''))];
  patternWordList.forEach((word) => {
    const matches = tagFreeContent.matchAll(
      new RegExp(
        `${patternList?.length ? '(?<!\\w)' : ''}${_.escapeRegExp(word)}${
          patternList?.length ? '(?!\\w)' : ''
        }`,
        `${patternList?.length ? 'i' : ''}g`,
      ),
    );
    const matchesArray = Array.from(matches);
    matchesArray.forEach((match) => {
      const start = match.index ?? 0;
      const end = start + word.length;
      matchList.push({
        word,
        position: { start, end },
      });
    });
  });

  return matchList.sort((a, b) => a.position.start - b.position.start);
};

/**
 * Finds each combination of matches for the pattern in the text.
 * Takes a match as the starting point, looks at the next match in the list until it has looked at every entry or all words of the pattern are found.
 * (When an entry is also in the pattern, the word gets crossed off from the list.)
 *
 * @param {IMatchProps[]} matchList - All matches of all words in the pattern.
 * @param {number} startIndex - The position in the list where we start looking.
 * @param {string} pattern - The pattern we are looking for.
 * @returns {ISuggestionProps | undefined} The suggestion that results from the words of the pattern that were found in the match list.
 */
const walkThroughList = (
  matchList: IMatchProps[],
  startIndex: number,
  pattern: string,
): ISuggestionProps | undefined => {
  const suggestionPositions: number[] = [];
  let searchWordList = getPatternWordList(pattern || '');
  const indices: number[] = [];
  const toleranceFactor = 0.5;
  const allowedMatchDistance = Math.round(pattern.length * toleranceFactor);

  matchList.forEach((_match, index) => {
    indices.push((startIndex + index) % matchList.length);
  });

  indices.forEach((i) => {
    const match = matchList[i];
    const listIndex = searchWordList.findIndex((word) => word === match.word);
    if (listIndex > -1) {
      const matchStartPosition = match.position.start;
      const matchEndPosition = match.position.end;

      if (
        Math.abs(matchStartPosition - matchList[startIndex].position.start) <=
          allowedMatchDistance &&
        Math.abs(matchEndPosition - matchList[startIndex].position.end) <=
          allowedMatchDistance &&
        !suggestionPositions.includes(matchStartPosition) &&
        !suggestionPositions.includes(matchEndPosition)
      ) {
        // remove search word from list
        searchWordList = searchWordList
          .slice(0, listIndex)
          .concat(searchWordList.slice(listIndex + 1, searchWordList.length));

        // add suggestion position
        for (
          let index = matchStartPosition;
          index <= matchEndPosition;
          index += 1
        ) {
          suggestionPositions.push(index);
        }
      }
    }
  });

  if (suggestionPositions.length === 0) {
    return undefined;
  }

  const start = Math.min(...suggestionPositions);
  const end = Math.max(...suggestionPositions);
  const foundPatternLength = pattern.length - _.join(searchWordList).length;
  const lengthDelta = Math.abs(foundPatternLength - (end - start));
  const missingWordsCount = searchWordList.length;

  if (foundPatternLength < 0 || lengthDelta < 0) {
    return undefined;
  }

  return {
    position: { start, end },
    lengthDelta,
    missingWordsCount,
  };
};

/**
 * Gets a suggestion for the lost element.
 *
 * @param {'mark' | 'a'} tagName - Used for intersection detection to determine whether there is already an existing area with the same element.
 * @param {string} content - The content that contains the element.
 * @param {string} pattern - The full pattern for exactly matching the suggestion.
 * @param {string[]} patternList - List of words from the pattern to find the most similar match.
 * @returns {ISuggestionCombinationProps | ISuggestionCombinationProps[] | null} Returns one `ContentArea` that is (ideally the most) similar to the pattern (case-sensitive matching)
 *  or multiple `ContentAreas` for full matches of each pattern in the list (case-insensitive matching).
 */
const findSuggestion = (
  tagName: 'mark' | 'a',
  content: string,
  pattern?: string,
  patternList?: string[],
): ISuggestionCombinationProps[] | null => {
  const contentObject = getTagfreeContent(content);
  const tagfreeContent = contentObject.content;
  const contentIndex = contentObject.indexes;
  const matchList = getMatchList(tagfreeContent, pattern, patternList);
  const patternWordCount = getPatternWordList(pattern || '')?.length;
  const fullMatchOnlyPatternWordCount = 3;
  let suggestions: ISuggestionProps[] = [];

  matchList.forEach((match, index) => {
    let newSuggestion;
    if (patternList?.length) {
      newSuggestion = {
        position: { start: match.position.start, end: match.position.end },
        lengthDelta: 0,
        missingWordsCount: 0,
        content: match.word,
      };
    }

    if (pattern?.length) {
      newSuggestion = walkThroughList(matchList, index, pattern);
    }

    if (newSuggestion) {
      const suggestionIntersectsExistingArea = getIntersectsExistingArea(
        contentIndex[newSuggestion.position.start],
        contentIndex[newSuggestion.position.end],
        content,
        tagName,
      );
      if (!suggestionIntersectsExistingArea) {
        suggestions = [...suggestions, newSuggestion];
      }
    }
  });

  const filteredCombinations = patternList?.length
    ? suggestions.filter(
        (s) => s.missingWordsCount === 0 && s.lengthDelta === 0,
      )
    : _.sortBy(
        suggestions.filter((s) =>
          patternWordCount <= fullMatchOnlyPatternWordCount
            ? s.missingWordsCount === 0
            : patternWordCount - s.missingWordsCount >=
              fullMatchOnlyPatternWordCount,
        ),
        [missingWordsCountPropName, lengthDeltaPropName],
      );

  const suggestionCombinations = filteredCombinations.map((c) => {
    const start = contentIndex[c.position.start];
    const end = contentIndex[c.position.end];
    return {
      position: { start, end },
      content: content.substring(start, end),
    };
  });

  if (suggestionCombinations.length < 1) {
    return null;
  }

  return patternList?.length
    ? suggestionCombinations
    : [suggestionCombinations[0]];
};

/**
 * Generates a `ContentArea` object from the pattern emphasized by `<br>` tags inside the HTML content string.
 *
 * Steps:
 * - Create a `Range` object starting after the start `<br>` tag and ending before the end `<br>` tag.
 * - Remove the `<br>` tags.
 * - Create a `ContentArea` object from the `Range` object.
 *
 * @param {string} newContent - The HTML content containing `<br>` tags indicating the start and end of the range.
 * @returns {ContentArea | undefined} A `ContentArea` object representing a `Range` object containing the pattern, or `undefined` if the conversion failed at any point.
 */
export const getUpdatedContentArea = (
  newContent: string,
): ContentArea | undefined => {
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(
    `<div id='${CALCULATION_CONTENT_CONTAINER_ID}'>${newContent}</div>`,
    'text/html',
  );

  const rangeStart = htmlDoc.getElementById(RANGE_START_ID);
  const rangeEnd = htmlDoc.getElementById(RANGE_END_ID);

  let range: Range | undefined = new Range();

  if (rangeStart && rangeEnd) {
    range.setStartAfter(rangeStart);
    range.setEndBefore(rangeEnd);
    rangeStart.remove();
    rangeEnd.remove();
  }

  range = preprocessRange(range, htmlDoc);
  htmlDoc.getElementById(CALCULATION_CONTENT_CONTAINER_ID)?.normalize();

  if (range) {
    return convertFromRange(range, CALCULATION_CONTENT_CONTAINER_ID, htmlDoc);
  }

  return undefined;
};

/**
 * Produces an HTML content string where a `<br>` tag emphasizes the beginning and the end of the pattern of the `ContentArea` object.
 *
 * @param {string} htmlContent - The HTML content that belongs to the `ContentArea` object which is supposed to be updated.
 * @param {ITagFreeContentProps} tagFreeContentObject - Tag-free content data generated from {@link htmlContent}.
 * @param {string} extendedPatternString - The pattern that was found in the content of {@link tagFreeContentObject}.
 * @param {number} precedingCharsLength - The length of preceding characters of the `ContentArea` object.
 * @param {number} subsequentCharsLength - The length of subsequent characters of the `ContentArea` object.
 * @param {number} patternLength - The length of the pattern of the `ContentArea` object.
 * @param {'mark' | 'a' | undefined} tagName - The tag name indicating what the `ContentArea` object is used for (mark or link).
 * @returns {string | null} The HTML content string with the emphasized position of the pattern (using `<br>` tags for start and end) or `null`
 *  if the pattern was not matched exactly once or the updated pattern already includes a `ContentArea` object.
 */
const getNewContent = (
  htmlContent: string,
  tagFreeContentObject: ITagFreeContentProps,
  extendedPatternString: string,
  precedingCharsLength: number,
  subsequentCharsLength: number,
  patternLength: number,
  tagName: ContentAreaTagName | undefined,
): string | null => {
  const contentIndex = tagFreeContentObject.indexes;
  const allMatches = Array.from(
    tagFreeContentObject.content.matchAll(
      new RegExp(_.escapeRegExp(extendedPatternString), 'g'),
    ),
  );

  if (allMatches?.length !== 1) {
    return null;
  }

  const index = allMatches[0]?.index ?? 0;
  const start = contentIndex[index + precedingCharsLength];
  const end = contentIndex[index + precedingCharsLength + patternLength];
  const matchInContent = htmlContent.substring(start, end);

  if (
    (tagName === 'mark' || tagName === 'a') &&
    getIntersectsExistingArea(start, end, htmlContent, tagName)
  ) {
    return null;
  }

  const precedingCharsInContent =
    htmlContent.substring(
      contentIndex[index],
      contentIndex[index + precedingCharsLength],
    ) ?? '';
  const subsequentCharsInContent =
    htmlContent.substring(
      contentIndex[index + precedingCharsLength + patternLength],
      contentIndex[
        index + precedingCharsLength + patternLength + subsequentCharsLength
      ],
    ) ?? '';

  return htmlContent.replace(
    precedingCharsInContent + matchInContent + subsequentCharsInContent,
    `${precedingCharsInContent}<br id='${RANGE_START_ID}'>${matchInContent}<br id='${RANGE_END_ID}'>${subsequentCharsInContent}`,
  );
};

/**
 * Updates a missing mark without asking for user feedback first.
 * Only allowed if at least the previous or subsequent characters plus the pattern match.
 *
 * Steps:
 * - Find the matching extended pattern, if existing (the frequency of pattern matches is not considered at this point).
 * - Create {@link newContent} where the extended pattern is marked (only if the pattern is matched exactly once) using {@link getNewContent}.
 * - Create an updated `ContentArea` object from {@link newContent} using {@link getUpdatedContentArea}.
 * - Validate the new `ContentArea` object using {@link getValidatedContentArea}.
 *
 * @param {string} htmlContent - The content the `ContentArea` object belongs to.
 * @param {ITagFreeContentProps} tagFreeContentObject - Object with 1. the tag-free content and 2. an array with the original character positions.
 * @param {ContentArea} contentArea - The `ContentArea` object that was not found at the original position.
 * @returns {ContentArea | null} The updated `ContentArea` object or `null` if an update was not possible.
 */
const getContentAreaFromTrustedSuggestion = (
  htmlContent: string,
  tagFreeContentObject: ITagFreeContentProps,
  contentArea: ContentArea,
): ContentArea | null => {
  if (
    contentArea.precedingCharacters == null ||
    contentArea.subsequentCharacters == null ||
    contentArea.patternString == null
  ) {
    return null;
  }

  const tagfreeContent = tagFreeContentObject.content;
  const fullyExtendedPatternString = `${contentArea.precedingCharacters ?? ''}${
    contentArea.patternString ?? ''
  }${contentArea.subsequentCharacters ?? ''}`;
  const inFrontExtendedPatternString = `${
    contentArea.precedingCharacters ?? ''
  }${contentArea.patternString ?? ''}`;
  const atEndExtendedPatternString = `${contentArea.patternString ?? ''}${
    contentArea.subsequentCharacters ?? ''
  }`;

  let precedingCharsLength = 0;
  let subsequentCharsLength = 0;
  let extendedPatternString;

  if (
    new RegExp(_.escapeRegExp(fullyExtendedPatternString), 'g').test(
      tagfreeContent,
    )
  ) {
    precedingCharsLength = contentArea.precedingCharacters?.length ?? 0;
    subsequentCharsLength = contentArea.subsequentCharacters?.length ?? 0;
    extendedPatternString = fullyExtendedPatternString;
  } else if (
    new RegExp(_.escapeRegExp(inFrontExtendedPatternString), 'g').test(
      tagfreeContent,
    )
  ) {
    precedingCharsLength = contentArea.precedingCharacters?.length ?? 0;
    extendedPatternString = inFrontExtendedPatternString;
  } else if (
    new RegExp(_.escapeRegExp(atEndExtendedPatternString), 'g').test(
      tagfreeContent,
    )
  ) {
    subsequentCharsLength = contentArea.subsequentCharacters?.length ?? 0;
    extendedPatternString = atEndExtendedPatternString;
  } else {
    return null;
  }

  const newContent = getNewContent(
    htmlContent,
    tagFreeContentObject,
    extendedPatternString,
    precedingCharsLength,
    subsequentCharsLength,
    contentArea.patternString?.length ?? 0,
    contentArea.tagName,
  );

  if (newContent) {
    return getValidatedContentArea(
      {
        ...contentArea,
        ...getUpdatedContentArea(newContent),
        autoUpdated: true,
        invalid: false,
        changed: true,
      },
      contentArea,
    );
  }

  return null;
};

export { findSuggestion, getContentAreaFromTrustedSuggestion };
